import { parseToolCallParamBindingRef } from '@cotera/utilities';
import {
  dottedPathFromRef,
  type UpstreamFieldOption,
  type UpstreamSourceGroup,
} from './variable-picker-data';

/** Resolves a picked upstream field into for-each node data. */
export function forEachDataFromUpstreamRef(ref: string): {
  itemsPath?: string;
  sourceRunNodeId?: string;
} {
  // The literal path with real indices — this is a runtime JSON pointer the saga
  // dereferences, NOT a label. Never the `[]`-collapsed display form
  // (`displayDottedPathFromRef`), which would not resolve at run time.
  const itemsPath = dottedPathFromRef(ref);
  // Root ref (empty pointer) → iterate the whole source value.
  const { sourceName, pointer } = parseToolCallParamBindingRef(ref);
  const data: { itemsPath?: string; sourceRunNodeId?: string } = {};
  if (pointer !== '') {
    data.itemsPath = itemsPath;
  }
  // Always record the source node's display name — the saga resolves it against
  // the ancestor outputs, so this works for the direct predecessor and any
  // earlier node alike (`sourceRunNodeId` holds a display name, not a run id).
  if (sourceName !== undefined && sourceName !== '') {
    data.sourceRunNodeId = sourceName;
  }
  return data;
}

/**
 * Finds an upstream field matching saved for-each path config, so the editor
 * can show the same chip as tool-call bindings.
 */
export function findUpstreamFieldForForEach(params: {
  sources: readonly UpstreamSourceGroup[];
  itemsPath: string | undefined;
  sourceRunNodeId: string | undefined;
}): UpstreamFieldOption | null {
  const { sources, itemsPath, sourceRunNodeId } = params;
  for (const group of sources) {
    if (sourceRunNodeId !== undefined && group.sourceName !== sourceRunNodeId) {
      continue;
    }
    for (const field of group.fields) {
      const { pointer } = parseToolCallParamBindingRef(field.ref);
      if (itemsPath === undefined || itemsPath.trim() === '') {
        if (pointer === '') {
          return field;
        }
        continue;
      }
      if (dottedPathFromRef(field.ref) === itemsPath) {
        return field;
      }
    }
  }
  return null;
}

/** Prefer array-valued fields when suggesting fan-out paths. */
export function preferArrayUpstreamSources(
  sources: readonly UpstreamSourceGroup[]
): readonly UpstreamSourceGroup[] {
  return sources.map((group) => {
    const arrays = group.fields.filter((field) => Array.isArray(field.value));
    if (arrays.length === 0) {
      return group;
    }
    const nonArrays = group.fields.filter(
      (field) => !Array.isArray(field.value)
    );
    return { ...group, fields: [...arrays, ...nonArrays] };
  });
}
